import { FragmentType, graphql, useFragment } from "@/gql"
import { ApplicationRolePreviewFragmentDoc } from "@/gql/graphql"
import { useMemo } from "react"

import { Text } from "@/elements"
import { CheckVerified } from "@/icons/outline"
import { BriefcaseIcon, UserIcon } from "@/icons/solid"
import { Tooltip } from "../Tooltip"
import { TextProps } from "@/elements/Text/Text"

graphql(`
  fragment ApplicationRolePreview on CreativeWorkPortfolioProject {
    creatorRoles {
      edges {
        node {
          name
          id
        }
      }
    }
  }
`)

interface Props {
  creativeWork: FragmentType<typeof ApplicationRolePreviewFragmentDoc> | undefined | null
  positionId: string | undefined
  withIcon?: boolean
  textProps?: TextProps
}

const defaultTextProps = {
  className: "whitespace-pre-line text-fg-2",
  variant: "caption" as const,
  maxLines: 1,
}

export const RolePreview: React.FC<Props> = ({ creativeWork, positionId, withIcon = true, textProps, ...props }) => {
  const roleData = useFragment(ApplicationRolePreviewFragmentDoc, creativeWork)
  const sortedRoles = useMemo(() => {
    return [
      ...(roleData?.creatorRoles?.edges ?? []).filter(r => r?.node.id === positionId),
      ...(roleData?.creatorRoles?.edges ?? []).filter(r => r?.node.id !== positionId),
    ]
  }, [positionId, roleData?.creatorRoles?.edges])

  const mergedTextProps = {
    ...defaultTextProps,
    ...textProps,
  }

  const rolesPreview = useMemo(() => {
    if (sortedRoles.length === 0) return <></>
    const additionalRoles = sortedRoles.length > 1 ? ` + ${sortedRoles.length - 1}` : ""
    return (
      <div className="flex w-fit flex-row items-center gap-x-[6px]">
        {withIcon && <UserIcon width={12} height={12} className="shrink-0 fill-fg-2 stroke-fg-2" />}
        <Text {...mergedTextProps}>
          {sortedRoles[0]?.node?.name}
          {additionalRoles}
        </Text>
        {positionId === sortedRoles[0]?.node?.id ? (
          <CheckVerified width={14} height={14} className="inline shrink-0 stroke-green-dark" />
        ) : null}
      </div>
    )
  }, [positionId, sortedRoles])

  return sortedRoles.length > 1 ? (
    <Tooltip
      trigger={rolesPreview}
      width={185}
      contentProps={{
        align: "start",
      }}>
      <div className="flex flex-col items-start">
        <Text variant="label" className="text-fg-2 underline">
          Roles
        </Text>

        {sortedRoles.map(roleEdge => (
          <div className="flex flex-row items-center gap-x-[6px]" key={roleEdge?.node?.id}>
            <Text variant="caption" className="text-fg-2">
              {roleEdge?.node?.name}
            </Text>
            {positionId === roleEdge?.node?.id ? <CheckVerified className="inline stroke-green-dark" /> : null}
          </div>
        ))}
      </div>
    </Tooltip>
  ) : (
    rolesPreview
  )
}
